import { useReaderLogStore } from '@/modules/FindControllerLog/store'
import { useGarageStore } from '@/modules/FindGarage'
import { useGetHubs } from '@/modules/Workstation/api/useGetHubs'
import { useWorkstationSelect } from '@/modules/Workstation/hooks/useWorkstationSelect'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { useMemo } from 'react'
import { useDateStore } from '@/components/DateInput'
import { useNightShiftInfo } from '@/modules/Auth/helpers/useNightShiftInfo'
import { isToday } from 'date-fns'

export const useGetRoutesForRole = () => {
    const { role } = useAuthStore()
    const { selectedRoutes } = useGarageStore()
    const { workstation: wsStore } = useWorkstationStore()
    const { selectedValue: wsSelect } = useWorkstationSelect()
    const { source } = useReaderLogStore()
    const { date } = useDateStore()
    const { isNightShift, isAfterMidnight } = useNightShiftInfo()

    const isTodayDate = isToday(date) || (isNightShift && isAfterMidnight())

    // Early return for MileageClerk - no need to fetch hubs or compute workstation
    const isMileageClerk = role === Role.MileageClerk
    const needsWorkstationData =
        !isMileageClerk && (role === Role.Controller || role === Role.PerformanceManager || role === Role.Reader)

    // Fetch live hubs and default hubs
    const { data: hubs, isLoading: isHubsLoading } = useGetHubs()
    const { data: defaultHubs, isLoading: isDefaultHubsLoading } = useGetHubs(true)

    // Use live hubs for today, default hubs for historical dates
    const activeHubs = isTodayDate ? hubs : defaultHubs
    const isActiveHubsLoading = isTodayDate ? isHubsLoading : isDefaultHubsLoading

    // Memoize workstation number calculation
    const wsNumber = useMemo(
        () => wsStore?.workstationNumber || wsSelect || source?.title,
        [wsStore?.workstationNumber, wsSelect, source?.title]
    )

    // Memoize workstation lookup
    const workstation = useMemo(() => {
        if (!needsWorkstationData || !activeHubs || isActiveHubsLoading || !wsNumber) {
            return null
        }

        for (const hub of activeHubs) {
            const foundWorkstation = hub.workstations.find((ws) => ws.workstationNumber === wsNumber)
            if (foundWorkstation) {
                return foundWorkstation
            }
        }
        return null
    }, [activeHubs, isActiveHubsLoading, wsNumber, needsWorkstationData])

    // Memoize the routes extraction
    const routes = useMemo(() => workstation?.routes.map((route) => route.route) || [], [workstation?.routes])

    // Early return patterns with memoized results
    return useMemo(() => {
        if (isMileageClerk) {
            return { allRoutes: selectedRoutes, allowEditAnnotations: false }
        }

        if (role === Role.Reader) {
            return { allRoutes: source?.routes.map((route) => route.route) || [], allowEditAnnotations: false }
        }

        if (needsWorkstationData) {
            return {
                allRoutes: routes,
                allowEditAnnotations: role === Role.Controller,
            }
        }

        return { allRoutes: [], allowEditAnnotations: false }
    }, [isMileageClerk, selectedRoutes, needsWorkstationData, routes, role, source?.routes])
}
